// Sales Partner Registration Page
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { usePartnerAuth } from '../../auth/PartnerAuthProvider';

export default function PartnerRegisterPage() {
  const { register } = usePartnerAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    companyName: '',
    password: '',
    confirmPassword: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.displayName.trim()) {
      setError('Full name is required.');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the Partner Terms & Conditions.');
      return;
    }

    try {
      setBusy(true);
      await register({
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
        phone: formData.phone,
        companyName: formData.companyName,
      });
      navigate('/partner/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="auth-modern d-flex flex-wrap min-vh-100">
      {/* Left Side - Branding */}
      <div
        className="auth-left-modern d-lg-flex d-none flex-column justify-content-center align-items-center p-5"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div className="position-relative z-1 text-center w-100" style={{ maxWidth: '500px' }}>
          <div className="mb-5">
            <Icon
              icon="mdi:handshake"
              style={{
                fontSize: '120px',
                color: 'white',
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
              }}
            />
          </div>

          <h1 className="text-white fw-bold mb-4" style={{ fontSize: '3rem' }}>
            RideWatch Partners
          </h1>
          <h2 className="text-white fw-bold mb-3" style={{ fontSize: '1.8rem' }}>
            Earn with Every Referral
          </h2>
          <p className="text-white opacity-90 fs-5 mb-5">
            Join our partner program and earn 10% recurring commission on every subscription
          </p>

          {/* Benefits */}
          <div className="d-flex justify-content-center gap-4 mt-5">
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:percent" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">10% Commission</small>
            </div>
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:refresh" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">Recurring</small>
            </div>
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:chart-line" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">Real-time Stats</small>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div
          className="position-absolute"
          style={{
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            top: '-100px',
            right: '-100px',
            zIndex: 0,
          }}
        />
        <div
          className="position-absolute"
          style={{
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            bottom: '50px',
            left: '-50px',
            zIndex: 0,
          }}
        />
      </div>

      {/* Right Side - Form */}
      <div
        className="auth-right-modern d-flex align-items-center justify-content-center p-4"
        style={{
          flex: 1,
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
        }}
      >
        <div className="w-100" style={{ maxWidth: '520px' }}>
          <div className="bg-white rounded-4 shadow-lg p-5">
            {/* Mobile Logo */}
            <div className="d-lg-none text-center mb-4">
              <Icon icon="mdi:handshake" style={{ fontSize: '60px', color: '#10b981' }} />
              <h2 className="fw-bold mt-2" style={{ color: '#10b981' }}>
                RideWatch Partners
              </h2>
            </div>

            <div className="text-center mb-4">
              <h3 className="fw-bold mb-2" style={{ color: '#2d3748' }}>
                Become a Sales Partner
              </h3>
              <p className="text-muted">
                Register to start earning commissions on referrals
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Full Name *
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:account" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="text"
                    name="displayName"
                    className="form-control ps-5 py-3"
                    placeholder="Enter your full name"
                    value={formData.displayName}
                    onChange={handleChange}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Email Address *
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:email" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="email"
                    name="email"
                    className="form-control ps-5 py-3"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Phone Number
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:phone" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control ps-5 py-3"
                    placeholder="+63 912 345 6789"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Company Name */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Company/Organization Name
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:domain" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="text"
                    name="companyName"
                    className="form-control ps-5 py-3"
                    placeholder="Your company (optional)"
                    value={formData.companyName}
                    onChange={handleChange}
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Password *
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:lock" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="password"
                    name="password"
                    className="form-control ps-5 py-3"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
                <small className="text-muted">At least 8 characters</small>
              </div>

              {/* Confirm Password */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Confirm Password *
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:lock-check" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="password"
                    name="confirmPassword"
                    className="form-control ps-5 py-3"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="mb-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="agreeTerms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="agreeTerms"
                    style={{ cursor: 'pointer', fontSize: '13px' }}
                  >
                    I agree to the{' '}
                    <Link to="/partner/terms" className="text-decoration-none fw-semibold" style={{ color: '#10b981' }}>
                      Partner Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-decoration-none fw-semibold" style={{ color: '#10b981' }}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger rounded-3 py-2" role="alert" style={{ fontSize: '14px' }}>
                  <Icon icon="mdi:alert-circle" className="me-2" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="btn w-100 py-3 fw-semibold text-white mb-3"
                disabled={busy}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                }}
              >
                {busy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:account-plus" className="me-2" style={{ fontSize: '18px' }} />
                    Create Partner Account
                  </>
                )}
              </button>

              {/* Sign In Link */}
              <div className="text-center mt-4">
                <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                  Already have a partner account?{' '}
                  <Link to="/partner/sign-in" className="text-decoration-none fw-bold" style={{ color: '#10b981' }}>
                    Sign In
                  </Link>
                </p>
              </div>

              {/* Regular User Link */}
              <div className="text-center mt-3">
                <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                  Looking to manage a school?{' '}
                  <Link to="/sign-up" className="text-decoration-none" style={{ color: '#667eea' }}>
                    Register as a Bus Company
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
